/**
 * Exam — the DMV-style simulator: same question count and 80% pass mark, no
 * feedback until the end, graded through the trainingExam seam (the set is pinned
 * at START; the score is computed from the pinned answer key at finish, never a
 * client-claimed number). Square exam cards, a quiet/celebratory result hero
 * (confetti on a perfect score), a missed-question review, and past attempts.
 * Ports renderExam. Attempts + stats flow through the progress context.
 */
import { useRef, useState } from 'react';
import { questions, tests, TEST_KEYS, CONTENT_VERSION, type TestKey } from '@/content/training';
import { useTrainingProgress } from '../TrainingProgressContext';
import { startExam, gradeExam, type ExamSpec, type ExamResult } from '../trainingExam';
import { apiStartExam } from '../trainingApi';
import { PageHeader, Card, Button } from './TrainingUI';

const LETTERS = ['A', 'B', 'C', 'D', 'E'];
const QBYID = new Map(questions.map((q) => [q.id, q]));

function shuffle<T>(a: T[]): T[] {
  const out = a.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function Confetti() {
  // lightweight CSS burst — pieces animated by the .training-confetti keyframe
  const pieces = Array.from({ length: 24 });
  const colors = ['#8FB560', '#E0556C', '#E7B416', '#4A90D9'];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {pieces.map((_, i) => (
        <span
          key={i}
          className="training-confetti"
          style={{
            left: `${(i / pieces.length) * 100}%`,
            background: colors[i % colors.length],
            animationDelay: `${(i % 6) * 0.12}s`,
          }}
        />
      ))}
    </div>
  );
}

export function Exam() {
  const progress = useTrainingProgress();
  const [spec, setSpec] = useState<ExamSpec | null>(null);
  const [idx, setIdx] = useState(0);
  const [picks, setPicks] = useState<Record<string, number>>({});
  // choice display order per pinned question (grading is by the original index)
  const [orders, setOrders] = useState<Record<string, number[]>>({});
  const [result, setResult] = useState<ExamResult | null>(null);
  // server attempt id when the exam was started + graded on the server (fix B);
  // null when running against the in-memory fallback (API unavailable).
  const serverAttemptRef = useRef<string | null>(null);
  // Guards against a double-tap firing start() twice before the first resolves.
  const startingRef = useRef(false);

  const applySpec = (s: ExamSpec) => {
    setSpec(s);
    setOrders(
      Object.fromEntries(
        s.questionIds.map((id) => [id, shuffle(QBYID.get(id)!.choices.map((_, i) => i))]),
      ),
    );
    setIdx(0);
    setPicks({});
    setResult(null);
  };

  const start = async (test: TestKey) => {
    // Re-entry guard: start() is async (a server round-trip), so a fast double-tap
    // — plausible on a touch device — could otherwise fire two apiStartExam calls
    // and leave a dangling server attempt / reset the UI mid-run. A ref is
    // synchronous, so the second tap is dropped before the first resolves.
    if (startingRef.current) return;
    startingRef.current = true;
    try {
      // Server pins the set + pass mark + content_version (fix B).
      const started = await apiStartExam(test);
      // Question ids are position-derived (gk_0…cv_199), so if the server pinned
      // its set from a DIFFERENT content_version than this (possibly stale) client
      // bundle — an open client spanning a content deploy — the ids won't align
      // with our questions and QBYID.get(id) could miss or map to the wrong
      // question, misgrading. Fall back to a fully self-consistent local exam; the
      // user picks up the new set on their next refresh.
      if (started.content_version !== CONTENT_VERSION) {
        serverAttemptRef.current = null;
        applySpec(startExam(test));
        return;
      }
      serverAttemptRef.current = started.id;
      applySpec({
        test,
        questionIds: started.question_ids,
        passMark: started.pass_mark,
        total: started.total,
        contentVersion: started.content_version,
        startedAt: Date.parse(started.started_at),
      });
    } catch {
      // persistence unavailable — grade locally (pre-TR-5 behaviour).
      serverAttemptRef.current = null;
      applySpec(startExam(test));
    } finally {
      startingRef.current = false;
    }
  };

  const finish = (finalPicks: Record<string, number>, s: ExamSpec) => {
    const answers = s.questionIds.map((id) => ({ questionId: id, choice: finalPicks[id] ?? null }));
    const r = gradeExam(s, answers);
    // feed the missed set + per-question stats (persists via events like study does)
    r.graded.forEach((g) => progress.recordAnswer(g.question, g.choice ?? -1));
    const attemptId = serverAttemptRef.current;
    progress.addExamAttempt({
      ...(attemptId ? { id: attemptId } : {}),
      test: r.test, score: r.score, total: r.total, passed: r.passed, pct: r.pct,
    });
    // When server-started, the SERVER records the authoritative graded attempt
    // (fix B — the recorded score is never client-reported); the local grade above
    // only drives this session's UI and equals the server's. The answers go through
    // the save queue, which sends them again until the server has them (ISS-055).
    if (attemptId) {
      progress.submitExamFinish(
        attemptId,
        s.questionIds.map((id) => ({ question_id: id, choice: finalPicks[id] ?? null })),
      );
    }
    setResult(r);
    setSpec(null);
  };

  // ---- result hero ----
  if (result) {
    const perfect = result.score === result.total;
    const wrong = result.graded.filter((g) => !g.correct);
    return (
      <div className="mx-auto max-w-2xl">
        <Card className="result-hero">
          {perfect && <Confetti />}
          <div className={`big ${result.passed ? 'verdict-pass' : 'verdict-fail'}`} style={{ position: 'relative' }}>
            {result.passed ? 'PASS' : 'NOT YET'}
          </div>
          <p style={{ position: 'relative' }}>
            You scored {result.score} of {result.total} ({result.pct}%). The DMV requires{' '}
            {tests[result.test].pass} correct.
          </p>
          <div className="mt-6 flex justify-center gap-3" style={{ position: 'relative' }}>
            <Button variant="primary" onClick={() => start(result.test)}>
              Another exam
            </Button>
            <Button variant="ghost" onClick={() => setResult(null)}>
              Exam menu
            </Button>
          </div>
        </Card>

        {wrong.length > 0 && (
          <Card className="mt-4">
            <h3 className="">
              Review your {wrong.length} missed question{wrong.length > 1 ? 's' : ''}
            </h3>
            <div>
              {wrong.map((g) => (
                <div key={g.question.id} className="review-item">
                  <div style={{ fontWeight: 600 }}>{g.question.q}</div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Your answer:{' '}
                    <span style={{ color: 'var(--red)' }}>
                      {g.choice != null ? g.question.choices[g.choice] : '(skipped)'}
                    </span>
                    <br />
                    Correct:{' '}
                    <span style={{ color: 'var(--navy)' }}>
                      {g.question.choices[g.question.answer]}
                    </span>
                  </div>
                  <p className="muted" style={{ marginTop: 4 }}>{g.question.exp}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  }

  // ---- running exam (no feedback) ----
  if (spec) {
    const qid = spec.questionIds[idx];
    const question = QBYID.get(qid);
    const t = tests[spec.test];
    const pick = (ci: number) => {
      const next = { ...picks, [qid]: ci };
      if (idx + 1 >= spec.total) finish(next, spec);
      else {
        setPicks(next);
        setIdx(idx + 1);
      }
    };
    return (
      <div className="mx-auto max-w-2xl">
        <div className="mb-3 flex items-center justify-between">
          <div className="q-progress">
            {t.name} exam · Question {idx + 1} of {spec.total}
          </div>
          <Button variant="ghost" className="small" onClick={() => setSpec(null)}>
            Abandon exam
          </Button>
        </div>
        <div className="exam-bar">
          <div style={{ width: `${(100 * idx) / spec.total}%` }} />
        </div>
        <Card>
          <div className="q-text">{question?.q}</div>
          <div>
            {(orders[qid] ?? question?.choices.map((_, i) => i) ?? []).map((ci, pos) => (
              <button
                key={ci}
                data-testid="exam-choice"
                onClick={() => pick(ci)}
                className="choice"
              >
                <span className="c-letter">{LETTERS[pos]}</span>
                <span>{question?.choices[ci]}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  // ---- exam menu ----
  const history = progress.examHistory;
  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="Exam Simulator"
        sub="The real thing: same question count, same 80% pass mark, no feedback until the end. California gives you 3 attempts per test before your application is voided — walk in over-prepared."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {TEST_KEYS.map((k) => {
          const t = tests[k];
          return (
            <Card key={k} className="flex flex-col">
              <h3 className="">{t.name}</h3>
              <p className="muted">{t.desc}</p>
              <p className="muted" style={{ marginTop: 6, fontSize: 12.5 }}>
                {t.qCount} questions · need {t.pass} correct · {t.mins}
              </p>
              <Button variant="primary" className="mt-4 self-start" onClick={() => start(k)}>
                Start {t.name} exam
              </Button>
            </Card>
          );
        })}
      </div>

      {history.length > 0 && (
        <Card className="mt-4">
          <h3 className="">Past attempts</h3>
          <div className="mt-3 space-y-1.5">
            {history
              .slice()
              .reverse()
              .map((e, i) => (
                <div key={i} className="muted">
                  {tests[e.test].name} — {e.score}/{e.total} ({e.pct}%) ·{' '}
                  <span
                    style={{
                      fontWeight: 700,
                      color: e.passed ? 'var(--navy)' : 'var(--red)',
                    }}
                  >
                    {e.passed ? 'PASS' : 'FAIL'}
                  </span>
                </div>
              ))}
          </div>
        </Card>
      )}
    </div>
  );
}
